import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Header from "../components/Header";
import DataTable from "react-data-table-component";
import Webcam from "react-webcam";
import { motion, AnimatePresence } from "framer-motion";

const AddDailyTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [images, setImages] = useState(Array(4).fill(null));
  const [uploadedFiles, setUploadedFiles] = useState(Array(4).fill(null));
  const webcamRef = useRef(null);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios.get("/user/tasks", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTasks(response.data.tasks);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    const fetchCompletedTasks = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios.get("/user/todayCompletedTasks", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCompletedTasks(response.data.tasks.map((task) => task.id));
      } catch (error) {
        console.error("Error fetching completed tasks:", error);
      }
    };

    fetchTasks();
    fetchCompletedTasks();
  }, []);

  const handleCompletedChange = (taskId, checked) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId
          ? {
              ...task,
              completed: checked,
              completed_date: checked
                ? new Date().toISOString().split("T")[0]
                : null,
            }
          : task
      )
    );
  };

  const handleDateChange = (taskId, newDate) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId ? { ...task, completed_date: newDate } : task
      )
    );
  };

  const handleSaveCompletedTasks = async () => {
    const selectedTasks = tasks
      .filter((task) => task.completed)
      .map((task) => ({ taskId: task.id, completedDate: task.completed_date }));

    if (selectedTasks.length === 0) {
      alert("No tasks selected!");
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");
      await axios.post("/user/dailyCompletedTasks", selectedTasks, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Completed tasks saved successfully!");
      setCompletedTasks((prev) => [
        ...prev,
        ...selectedTasks.map((t) => t.taskId),
      ]);
    } catch (error) {
      console.error("Error saving completed tasks:", error);
      alert("Failed to save tasks.");
    }
  };

  const captureImage = (index) => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setImages((prevImages) => {
        const newImages = [...prevImages];
        newImages[index] = imageSrc;
        return newImages;
      });
    }
  };

  const handleFileUpload = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedFiles((prevFiles) => {
          const newFiles = [...prevFiles];
          newFiles[index] = reader.result;
          return newFiles;
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImages = async () => {
    const token = localStorage.getItem("accessToken");
    const formData = new FormData();

    images.forEach((image, index) => {
      if (image) {
        const blob = dataURItoBlob(image);
        formData.append("images", blob, `webcam_image${index + 1}.jpg`);
      }
    });

    uploadedFiles.forEach((file, index) => {
      if (file) {
        const blob = dataURItoBlob(file);
        formData.append("images", blob, `uploaded_image${index + 1}.jpg`);
      }
    });

    try {
      const response = await axios.post("/user/uploadDisplayImages", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Upload successful", response.data);
      alert("Images uploaded successfully!");
    } catch (error) {
      console.error("Error uploading images:", error);
    }
  };

  const dataURItoBlob = (dataURI) => {
    let byteString = atob(dataURI.split(",")[1]);
    let mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    let arrayBuffer = new ArrayBuffer(byteString.length);
    let uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    return new Blob([uint8Array], { type: mimeString });
  };

  return (
    <motion.main
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Header />

      <motion.div
        className="container mt-5"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <motion.h1
          className="text-center mb-4"
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          Add Daily Tasks
        </motion.h1>

        <motion.div
          className="tasks-table"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="mt-5">Tasks List</h2>
          <DataTable
            columns={[
              { name: "Task Name", selector: (row) => row.name, sortable: true },
              {
                name: "Completed",
                cell: (row) => (
                  <input
                    type="checkbox"
                    checked={row.completed || false}
                    onChange={(e) =>
                      handleCompletedChange(row.id, e.target.checked)
                    }
                    disabled={completedTasks.includes(row.id)}
                  />
                ),
              },
              {
                name: "Completed Date",
                cell: (row) => (
                  <input
                    type="date"
                    value={row.completed_date || ""}
                    onChange={(e) =>
                      handleDateChange(row.id, e.target.value)
                    }
                    disabled={!row.completed || completedTasks.includes(row.id)}
                  />
                ),
              },
            ]}
            data={tasks}
            pagination
            highlightOnHover
            striped
            responsive
          />
        </motion.div>

        <motion.div
          className="mt-4 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <input
            type="checkbox"
            id="showImageOptions"
            onChange={() => setShowImageOptions(!showImageOptions)}
          />
          <label htmlFor="showImageOptions" className="ml-2">
            Image on Display
          </label>
        </motion.div>

        <AnimatePresence>
          {showImageOptions && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4"
            >
              <div className="text-center">
                <button
                  className="btn btn-primary"
                  onClick={() => setCameraEnabled(!cameraEnabled)}
                >
                  {cameraEnabled ? "Stop Camera" : "Start Camera"}
                </button>
              </div>

              {cameraEnabled && (
                <motion.div
                  className="mt-3 text-center"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                >
                  <Webcam
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{ facingMode: "user" }}
                    className="mb-3"
                  />
                  {[...Array(4)].map((_, index) => (
                    <div key={index} className="mb-2">
                      <button
                        className="btn btn-secondary"
                        onClick={() => captureImage(index)}
                      >
                        Capture Image {index + 1}
                      </button>
                      {images[index] && (
                        <img
                          src={images[index]}
                          alt={`Captured ${index + 1}`}
                          width={100}
                          height={100}
                          className="ml-2"
                        />
                      )}
                    </div>
                  ))}
                </motion.div>
              )}

              <div className="mt-3 text-center">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="mb-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, index)}
                    />
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          className="mt-4 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <button
            className="btn btn-success m-2"
            onClick={handleSaveCompletedTasks}
          >
            Save Completed Tasks
          </button>
          <button
            className="btn btn-primary m-2"
            onClick={handleUploadImages}
          >
            Upload Images
          </button>
        </motion.div>
      </motion.div>
    </motion.main>
  );
};

export default AddDailyTasks;
